const path = require('path');
const loglevel = require('loglevel');
const prefixer = require('loglevel-plugin-prefix');
const stripAnsi = require('strip-ansi');
const { map, replace, get, sample, filter, find } = require('lodash');
const humanize = require('ms');

// Marks an Error already reported to New Relic, so the log.error hook below does not count the same
// failure a second time.
//
// A NON-ENUMERABLE STRING KEY, not a symbol. Bull rebuilds a sandboxed processor's error in the
// parent from Object.getOwnPropertyNames, which carries a string key and drops a symbol. With a
// symbol the parent cannot tell a processor throw, which the child already noticed, from a
// Bull-level failure that nothing noticed, so it either double-counts every job error or loses every
// stall. Sentry's own `__sentry_captured__` has this shape for the same boundary.
//
// Non-enumerable is as far as hiding it goes, and it does NOT survive that boundary. Measured:
// bull/lib/process/master.js installs an Error.prototype.toJSON that copies every
// getOwnPropertyNames key, so inside a jobs process JSON.stringify prints the mark; and
// sandbox.js rebuilds the parent's error with Object.assign, which makes it an ENUMERABLE own
// property there, so util.inspect prints it in a log line. `__sentry_captured__` already arrives the
// same way on the same errors. A parent-side reporter that must not carry it builds a fresh Error
// instead: see reportableCopy in packages/itrvl-jobs/src/report-job-error.js.
const newRelicNoticed = '__itrvlNewRelicNoticed';

const markNewRelicNoticed = err => {
  if (err instanceof Error) Object.defineProperty(err, newRelicNoticed, { value: true, configurable: true, writable: true });
  return err;
};

// setup() has no idempotence guard, and a second call would stack a second hook on loglevel's
// methodFactory, so every log.error would report twice. This holds for the New Relic hook only; the
// Sentry hook above keeps its existing behaviour.
let newRelicHookInstalled = false;

function enableMatching(logger, env, level) {
  if (env && env != '') {
    const parts = env.split(/[ ,]/);
    map(parts, part => {
      const pattern = '^' + replace(part, '*', '.*') + '$';
      const test = new RegExp(pattern);
      if (logger.name.match(test)) {
        logger.setLevel(level);
      }
    });
  }
}

// In test we return a constant logger
// to make it easy to mock and verify
// logging stuff and silence
// logging output durring tests
let theTestLogger;
const testLogger = () => {
  if (!theTestLogger) {
    theTestLogger = {
      name: 'itrvl:test:logger',
      setLevel: () => {},
      trace: () => {},
      debug: () => {},
      info: () => {},
      warn: () => {},
      error: () => {},
      time: () => {},
      timeEnd: () => {},
    };
  }

  return theTestLogger;
};

// Flag to let us know we are testing the logger code
let loggerTest = process.env.LOG_TESTS || false;

const depth = 5;

const logger = function (loggerName) {
  const getGlobalThis = () => {
    if (typeof globalThis !== 'undefined') return globalThis;
    if (typeof self !== 'undefined') return self;
    if (typeof window !== 'undefined') return window;
    if (typeof global !== 'undefined') return global;
    if (typeof this !== 'undefined') return this;
    throw new Error('Unable to locate global `this`');
  };

  const g = getGlobalThis();

  const get = () => {
    const orig = Error.prepareStackTrace;
    Error.prepareStackTrace = (_, stack) => stack;
    let { stack } = new Error();
    Error.prepareStackTrace = orig;
    // On Safari the stack comes as a big string instead of
    // an array so let's break it up into an array like
    // we get on Chrome and emulate Chrome's CallSite
    if (typeof stack == 'string') {
      stack = map(stack.split('\n'), line => {
        line = line.replace(/https?:\/\/[^\/]+\//, '');
        return {
          getTypeName: () => {
            return line.split('@')[0].split('/')[0];
          },
          getMethodName: () => {
            const parts = line.replace('<', '').split('@')[0].split('/');
            if (parts.length > 1) {
              return parts[1];
            }
            return 'Unknown';
          },
          getLineNumber: () => {
            const parts = line.split(':');
            if (parts.length > 2) {
              return line.split(':')[2];
            }
            return 'Unknwon';
          },
          getFunctionName: () => {
            const func = line.split('@')[0];
            // If it has a slash in it then assume it is a method / function pair on Firefox
            if (func.includes('/')) {
              return null;
            }
            return func;
          },
          getFileName: () => {
            const parts = line.split('@');
            if (parts.length > 1) {
              return parts[1].split(':')[0];
            }
            return 'Unknown';
          },
        };
      });
    }
    return stack;
  };

  const stackUnroll = (d = depth) => {
    return __stack[d] || stackUnroll(d--);
  };

  if (Object.getOwnPropertyDescriptor(g, '__stack') === undefined) {
    Object.defineProperty(g, '__stack', {
      get,
    });
  }

  if (Object.getOwnPropertyDescriptor(g, '__line') === undefined) {
    Object.defineProperty(g, '__line', {
      get: () => stackUnroll(depth).getLineNumber(),
    });
  }

  if (Object.getOwnPropertyDescriptor(g, '__function') === undefined) {
    Object.defineProperty(g, '__function', {
      get: () => stackUnroll(depth).getFunctionName(),
    });
  }

  if (Object.getOwnPropertyDescriptor(g, '__file') === undefined) {
    Object.defineProperty(g, '__file', {
      get: () => stackUnroll(depth).getFileName(),
    });
  }

  if (!loggerName) {
    loggerName = 'server';
  } else {
    // Figure out what the root of our stuff is
    let prefix;
    let pkgName = '';
    // Is this a lambda or package?
    if (loggerName.includes('/lambda/')) {
      // Find the lambda prefix
      prefix = loggerName.substring(0, loggerName.indexOf('/', loggerName.indexOf('/lambda/') + 8));
    } else if (loggerName.includes('/packages/')) {
      // Find the package prefix
      prefix = loggerName.substring(0, loggerName.indexOf('/', loggerName.indexOf('/packages/') + 10));
      pkgName = prefix.slice(prefix.lastIndexOf('/') + 1).replace('itrvl-', '');
    } else {
      prefix = '/';
    }
    loggerName =
      pkgName +
      loggerName
        .replace(prefix, '')
        .replace(/^\/?\.\./, '')
        .replace(/^\/?src/, '')
        .replace(/^\/?lib/, '')
        .replace(/^\/?function/, '')
        // Swap slash for colons
        .replace(/\//g, ':')
        // Strip any double colons
        .replace(/::/, ':')
        // And strip the extension
        .replace(/\.js$/, '');
  }
  if (!loggerName.startsWith('itrvl:')) {
    loggerName = 'itrvl:' + path.basename(loggerName, '.js');
  }
  let logger;

  // Return testLogger in all tests EXCEPT the loggerTest
  if ((process.env.NODE_ENV === 'test' || process.env.NODE_ENV === 'testdirect') && !loggerTest) {
    logger = testLogger();
  } else {
    logger = loglevel.getLogger(loggerName);

    // Setup methods around console.time and timeEnd
    logger.time = (...args) => {
      // if (logger.getLevel() <= loglevel.levels.DEBUG) {
      console.time(...args);
      // }
    };
    logger.timeEnd = (...args) => {
      // if (logger.getLevel() <= loglevel.levels.DEBUG) {
      console.timeEnd(...args);
      // }
    };
  }

  // Give users easy access to levels
  logger.levels = loglevel.levels;

  // Enable matching but higher levels take priority.
  // i.e DEBUG="itrvl:*" INFO="itrvl:*:itinerary" means everything except itinerary logs at DEBUG
  map(['TRACE', 'DEBUG', 'INFO', 'WARN', 'ERROR'], level => {
    enableMatching(logger, process.env[level], loglevel.levels[level]);
    enableMatching(logger, process.env[`REACT_APP_${level}`], loglevel.levels[level]);
  });

  // Give us a stack trace that excludes vendored stuff in the browser and this function itself
  // prettier-ignore
  logger.stackTrace = () => map(
    filter(get().slice(2), cs => cs.getFileName() && !cs.getFileName().includes('vendor')),
    cs => cs.getFunctionName() ? cs.getFunctionName() : `${cs.getTypeName()}.${cs.getMethodName()}`
  );

  return logger;
};

// Exposed for testing
if (process.env.NODE_ENV !== 'production') {
  logger._enableMatching = enableMatching;
}

// Expose some things at the root
logger.levels = loglevel.levels;
logger.setDefaultLevel = loglevel.setDefaultLevel;
logger.getLoggers = loglevel.getLoggers;
logger.getLogger = loglevel.getLogger;

logger.setup = function (server = true, useSentry = true, { detachSentryScope = false } = {}) {
  let defaultLevel = loglevel.levels.WARN;
  switch (get(process.env, 'ITRVL_ENV', get(process.env, 'REACT_APP_ITRVL_ENV', 'production'))) {
    case 'integration':
    case 'hungary':
    case 'staging':
    case 'sandbox':
    case 'development':
      defaultLevel = loglevel.levels.INFO;
      break;
  }
  loglevel.setDefaultLevel(defaultLevel);

  const sentryDsn =
    process.env.SENTRY_API_DSN ||
    process.env.SENTRY_JOBS_DSN ||
    process.env.REACT_APP_SENTRY_AGENT_DSN ||
    process.env.REACT_APP_SENTRY_CLIENT_DSN ||
    process.env.SENTRY_EXPRESS_AGENT_DSN ||
    process.env.SENTRY_EXPRESS_CLIENT_DSN;

  if (!process.env.REACT_APP_BYPASS_SENTRY_NODE && sentryDsn && useSentry) {
    const Sentry = require('./sentry');
    // A process whose async context does not reach the code that logs sets detachSentryScope, so an
    // event carries nothing from a scope that belongs to another request. The rest capture through the
    // ambient scope, which is where jobs puts the tags and contexts of the job being run.
    const capture = detachSentryScope ? require('./detached') : Sentry;
    const originalFactory = loglevel.methodFactory;
    loglevel.methodFactory = function (methodName, logLevel, loggerName) {
      const rawMethod = originalFactory(methodName, logLevel, loggerName);

      let strippedMessage; // for stripping ANSI from sentry message when in development

      return function () {
        const message = Object.keys(arguments).map((key, index) => arguments[key]);
        switch (methodName) {
          case 'error': {
            // Two paths on purpose. An Error carries a stack and a type, so it goes through
            // captureException and Sentry groups it by the throw site. A bare string has neither, so
            // it can only be a message — sent at error level, grouped by its text.
            //
            // The Error is searched for in any position: both `log.error(err)` and the more common
            // `log.error('some label: ', err)` are idiomatic in this repo.
            //
            // A call site that reports the same Error itself, with richer context, needs no
            // suppression here: the SDK marks an Error on capture and drops every later capture of
            // that object. The capture that runs FIRST is the one that reaches Sentry.
            const thrown = find(message, m => m instanceof Error);
            if (thrown) {
              capture.captureException(thrown);
              break;
            }
            // A printf-style call gets Sentry's native message grouping. Sending the template
            // alongside its values makes the TEMPLATE the group, so a value interpolated into the
            // text — a Bull job id, an object id — stops opening a new issue per value, and no
            // fingerprint is set anywhere. The console substitutes %s itself, so the call site stays
            // an ordinary string and reads the same in a terminal.
            //
            // The count must match exactly. A mismatch would leave placeholders in the text or drop
            // values, so it falls through to the plain path instead.
            const [template, ...values] = message;
            if (typeof template === 'string' && values.length && template.includes('%s')) {
              const parts = stripAnsi(template).replace(/>.*?>/, '').trim().split('%s');
              if (parts.length - 1 === values.length) {
                parts.raw = parts; // parameterize is a tag function: it needs .raw and .join
                capture.captureMessage(Sentry.parameterize(parts, ...values), 'error');
                break;
              }
            }
            strippedMessage = map(message, m => {
              if (typeof m === 'string') {
                return stripAnsi(m).replace(/>.*?>/, '');
              }
              return m;
            });
            capture.captureMessage(strippedMessage.join(' '), 'error');
            break;
          }
          default:
            // Sentry.captureEvent for log.warn is very noisy and doesn't properly preserve request context.
            break;
        }
        rawMethod.apply(undefined, arguments);
      };
    };
    loglevel.setLevel(loglevel.getLevel()); // Be sure to call setLevel method in order to apply plugin.
  }

  switch (process.env.NODE_ENV) {
    case 'production':
      prefixer.reg(loglevel);
      prefixer.apply(loglevel, {
        format(level, name, timestamp) {
          const pad = level.length < 5 ? ' ' : '';
          return `> ${timestamp} ${level.toUpperCase()}${pad} > ${name}`;
        },
      });
      loglevel.setDefaultLevel(loglevel.levels.WARN);
      break;
    case 'test':
      break;
    default:
      if (server) {
        const chalk = require('chalk');

        // Cache of color names for our loggers
        const colorNames = {};

        // Colors chalk offers that look okay
        // on both black and white backgrounds
        const availableColors = [
          'red',
          'green',
          'yellow',
          'blue',
          'magenta',
          'cyan',
          'redBright',
          'greenBright',
          'yellowBright',
          'blueBright',
          'magentaBright',
          'cyanBright',
        ];

        // Colors for log levels
        const levelColors = {
          TRACE: chalk.magenta,
          DEBUG: chalk.cyan,
          INFO: chalk.green,
          WARN: chalk.yellow,
          ERROR: chalk.red,
        };

        const getColorForName = name => {
          let nameColor = colorNames[[name]];
          if (!nameColor) {
            // Assign a random color
            nameColor = colorNames[name] = sample(availableColors);
          }
          return nameColor;
        };

        let prevTime;
        prefixer.reg(loglevel);
        prefixer.apply(loglevel, {
          format(level, name, timestamp) {
            const func = __function ? ` ${__function}() ` : ' ';
            const line = __line ? `${__line}` : '';
            const filePath = __file ? `${path.basename(__file)}` : '';
            const fileInfo = filePath ? `${filePath}:${line}${func}` : '';

            const extendedInfo = process.env.EXTENDED_LOGGING ? fileInfo : name;

            const nameColor = getColorForName(name);
            const pad = level.length < 5 ? ' ' : '';
            const curr = Date.now();
            const ms = curr - (prevTime || curr);
            prevTime = curr;
            return `> ${levelColors[level.toUpperCase()].inverse(level)}${pad} ${chalk[nameColor](humanize(ms))}\t> ${extendedInfo}\t`;
          },
        });
      } else {
        prefixer.reg(loglevel);
        if (process.env.NODE_ENV === 'development') {
          prefixer.apply(loglevel, {
            format(level, name, timestamp) {
              return `> ${name} >`;
            },
          });
        } else {
          prefixer.apply(loglevel, {
            format(level, name, timestamp) {
              const pad = level.length < 5 ? ' ' : '';
              return `> [${timestamp}] ${level.toUpperCase()}${pad} ${name} >`;
            },
          });
        }
      }
      break;
  }
};

// Visible for testing
logger.setLoggerTest = val => (loggerTest = val);

// New Relic gets the same reach as Sentry: one hook, rather than a noticeError at every call site.
//
// The agent arrives as an argument instead of a require. agent and client bundle this module for the
// browser, and a static `require('newrelic')` would pull the node agent into a webpack build, where it
// cannot run. Each instrument-newrelic.js calls this instead, and those files already exist to be the
// first require of their process.
//
// Order does not matter. This wraps loglevel's methodFactory, and so does setup(); whichever runs
// second wraps the first, and both call through.
//
// noticeError attaches to whatever transaction is active when it runs, so a log.error inside a web
// request or a job lands on that transaction. A call with no transaction is still recorded, without
// an association.
logger.useNewRelic = function (newrelic) {
  if (!newrelic || newRelicHookInstalled) return;
  newRelicHookInstalled = true;

  const originalFactory = loglevel.methodFactory;
  loglevel.methodFactory = function (methodName, logLevel, loggerName) {
    const rawMethod = originalFactory(methodName, logLevel, loggerName);

    return function () {
      if (methodName === 'error') {
        const message = Object.keys(arguments).map(key => arguments[key]);
        const thrown = find(message, m => m instanceof Error);
        if (thrown) {
          if (!thrown[newRelicNoticed]) {
            markNewRelicNoticed(thrown);
            newrelic.noticeError(thrown);
          }
        } else {
          // A bare string carries no stack, so New Relic groups it by the message text. ANSI is
          // stripped for the same reason Sentry strips it: a colourised local message would group
          // apart from the identical deployed one.
          const clean = m => (typeof m === 'string' ? stripAnsi(m).replace(/>.*?>/, '') : m);

          // `log.error('failed for %s', id)` has to group by the TEMPLATE, or a per-invocation value
          // opens a new group on every call. New Relic offers no fingerprint hook and groups an error
          // by its message, so the template becomes the message and the values become attributes,
          // where NRQL can still read them. This is the same split the Sentry hook makes, and the
          // same guard: a count mismatch would leave placeholders in the text or drop values, so it
          // falls through to the joined form instead.
          const [template, ...values] = message;
          const parameterised = typeof template === 'string' && values.length && template.includes('%s') ? clean(template).trim() : null;

          if (parameterised && parameterised.split('%s').length - 1 === values.length) {
            newrelic.noticeError(new Error(parameterised), { 'log.params': values.map(v => String(clean(v))).join(' | ') });
          } else {
            newrelic.noticeError(new Error(map(message, clean).join(' ')));
          }
        }
      }
      rawMethod.apply(undefined, arguments);
    };
  };
  loglevel.setLevel(loglevel.getLevel());

  // setLevel on the root rebuilds the root alone. A named logger captures the root methodFactory when
  // getLogger(name) runs, and rebuild() then calls the logger's OWN captured factory, so an existing
  // child keeps the factory it was born with and never reaches noticeError.
  //
  // The api and its three crons all call logger.setup(), take their own logger, and only then require
  // server.js, which is what installs this hook. So they are exactly the loggers that need retrofit.
  // Each child holds the pre-hook root factory, and the new root factory wraps that same factory, so
  // handing every child the new root loses nothing. itrvl-logger never sets a per-logger factory, so
  // there is no custom factory to overwrite.
  const rootFactory = loglevel.methodFactory;
  Object.values(loglevel.getLoggers()).forEach(child => {
    child.methodFactory = rootFactory;
    child.rebuild();
  });
};

logger.newRelicNoticed = newRelicNoticed;
logger.markNewRelicNoticed = markNewRelicNoticed;

module.exports = logger;
